import { FC, type MouseEvent, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Card, Col, Form, Nav, Row, Spinner } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import CaresPageHeader from '../shell/cares-page-header';
import { getCaresShellBootstrap } from '../shell/bootstrap';
import {
  scheduleSettingsSections,
  type ScheduleSettingsSection,
} from './config';
import AdvancedReadOnlyWorkspace from './advanced-read-only-workspace';
import ImportWizardWorkspace, { type BulkWorksheetKind } from './import-wizard-workspace';
import ImportContractPanel from './import-contract-panel';
import ResourceCrudWorkspace from './resource-crud-workspace';
import EvidenceReviewWorkspace, { type RuleCategory } from './evidence-review-workspace';
import StructureRosterWorkspace from './structure-roster-workspace';
import { LocationHelpTip } from './group-location-workspace';
import { updateLastSelectedScheduleGroup } from '../group-schedule/view-preferences-api';
import './schedule-settings-page.scss';

type ScheduleSettingsPageProps = {
  sectionId: string;
};

type ScheduleSettingsOverviewResponse = {
  selected_company: {
    id: number;
    name: string;
    slug: string;
  };
  available_companies: Array<{
    id: number;
    name: string;
    slug: string;
  }>;
  selected_group: {
    id: number;
    name: string;
    slug: string;
    kind: string;
    department: { id: number; name: string; slug: string } | null;
  } | null;
  available_groups: Array<{
    id: number;
    name: string;
    slug: string;
    kind: string;
    department: { id: number; name: string; slug: string } | null;
  }>;
  group_portfolio: Array<{
    id: number;
    name: string;
    slug: string;
    kind: string;
    department: { id: number; name: string; slug: string } | null;
    readiness_status: string;
    draft_candidates: number;
    pending_review: number;
    imports: number;
    blockers: string[];
    operations: {
      published_through: string | null;
      next_draft: null | { id: string; status: string; period_start: string | null; period_end: string | null };
      request_counts: Record<string, number>;
      chronos_run: null | { run_id: string; status: string; review_state: string; period_start: string; period_end: string; produced_at: string; publish_allowed: boolean };
    };
  }>;
  unassigned_onboarding: {
    visible: boolean;
    candidates: number;
    message: string;
  };
  counts: {
    import_batches_total: number;
    locations_total: number;
    locations_active: number;
    services_total: number;
    providers_total: number;
    providers_active: number;
    roles_total: number;
    eligibility_total: number;
    availability_blocks_total: number;
    targets_total: number;
    holidays_total: number;
    rule_sets_total: number;
    constraints_total: number;
    coverage_templates_total: number;
    review_candidates_total: number;
    review_candidates_pending: number;
    review_conflicts_unresolved: number;
  };
  readiness: {
    configured_sections: number;
    total_sections: number;
    score: number;
    blockers: string[];
  };
  operations: null | {
    published_through: string | null;
    next_draft: null | { id: string; status: string; period_start: string | null; period_end: string | null };
    request_counts: Record<string, number>;
    chronos_run: null | { run_id: string; status: string; review_state: string; period_start: string; period_end: string; produced_at: string; publish_allowed: boolean };
  };
};

const toneClassMap: Record<ScheduleSettingsSection['metrics'][number]['tone'], string> = {
  primary: 'primary',
  success: 'success',
  warning: 'warning',
  info: 'info',
};

const advancedReadOnlySectionIds = new Set([
  'eligibility',
  'time-off',
  'targets',
  'rules',
]);

const displayScheduleName = (name: string) => name.replace(/\s+schedule$/i, '').trim();
const ruleFilters: Array<{ id: RuleCategory; label: string }> = [
  { id: 'all', label: 'All Rules' },
  { id: 'hard', label: 'Hard Rules' },
  { id: 'soft', label: 'Soft Rules' },
  { id: 'preference', label: 'Preferences' },
  { id: 'other', label: 'Other' },
];

const bulkWorksheetKinds: Partial<Record<string, BulkWorksheetKind>> = {
  locations: 'locations',
  services: 'services',
  providers: 'providers',
  'roles-shifts': 'roles',
  eligibility: 'eligibility',
  'time-off': 'time-off',
  targets: 'targets',
  rules: 'rules',
};

const sectionMetrics = (
  section: ScheduleSettingsSection,
  overview: ScheduleSettingsOverviewResponse | null
) => {
  if (!overview) {
    return section.metrics;
  }

  const { counts, readiness } = overview;

  switch (section.id) {
    case 'overview':
      return [];
    case 'imports':
      return [
        { label: 'Import runs', value: `${counts.import_batches_total}`, tone: 'primary' as const },
        { label: 'Preview rows', value: 'Seeded baseline', tone: 'info' as const },
        { label: 'Accepted workbook types', value: '2', tone: 'success' as const },
      ];
    case 'locations':
      return [
        { label: 'Active locations', value: `${counts.locations_active}`, tone: 'primary' as const },
        { label: 'Services linked', value: `${counts.services_total}`, tone: 'info' as const },
        { label: 'Coverage warnings', value: counts.locations_total > 0 ? '0' : '1', tone: 'success' as const },
      ];
    case 'providers':
      return [
        { label: 'People loaded', value: `${counts.providers_total}`, tone: 'primary' as const },
        { label: 'People active', value: `${counts.providers_active}`, tone: 'success' as const },
        { label: 'Alias conflicts', value: '0', tone: 'warning' as const },
      ];
    case 'services':
      return [
        { label: 'Services loaded', value: `${counts.services_total}`, tone: 'primary' as const },
        { label: 'Locations available', value: `${counts.locations_total}`, tone: 'info' as const },
        { label: 'Provider dependencies', value: counts.providers_total > 0 ? 'Ready' : 'Pending', tone: 'success' as const },
      ];
    case 'roles-shifts':
      return [
        { label: 'Role families', value: `${counts.roles_total}`, tone: 'primary' as const },
        { label: 'Shift templates', value: `${counts.coverage_templates_total}`, tone: 'info' as const },
        { label: 'Alias bundles', value: `${counts.constraints_total}`, tone: 'warning' as const },
      ];
    case 'eligibility':
      return [
        { label: 'Eligibility rows', value: `${counts.eligibility_total}`, tone: 'primary' as const },
        { label: 'Hard exclusions', value: '0', tone: 'warning' as const },
        { label: 'Validated mappings', value: counts.eligibility_total > 0 ? '100%' : '0%', tone: 'info' as const },
      ];
    case 'time-off':
      return [
        { label: 'Blocked dates', value: `${counts.availability_blocks_total}`, tone: 'primary' as const },
        { label: 'Admin blocks', value: '0', tone: 'info' as const },
        { label: 'Conflict warnings', value: '0', tone: 'success' as const },
      ];
    case 'targets':
      return [
        { label: 'Target sets', value: `${counts.targets_total}`, tone: 'primary' as const },
        { label: 'Fairness rules', value: `${counts.rule_sets_total}`, tone: 'warning' as const },
        { label: 'Variance alerts', value: '0', tone: 'info' as const },
      ];
    case 'rules':
      return [
        { label: 'Hard rules', value: `${counts.constraints_total}`, tone: 'primary' as const },
        { label: 'Soft rules', value: `${counts.rule_sets_total}`, tone: 'info' as const },
        { label: 'Bundle presets', value: `${counts.rule_sets_total}`, tone: 'warning' as const },
      ];
    case 'coverage-templates':
      return [
        { label: 'Templates', value: `${counts.coverage_templates_total}`, tone: 'primary' as const },
        { label: 'Slots modeled', value: `${counts.roles_total}`, tone: 'info' as const },
        { label: 'Coverage gaps', value: '0', tone: 'success' as const },
      ];
    case 'validation':
      return [
        { label: 'Critical issues', value: `${readiness.blockers.length}`, tone: readiness.blockers.length === 0 ? ('success' as const) : ('warning' as const) },
        { label: 'Warnings', value: `${readiness.blockers.length}`, tone: 'warning' as const },
        { label: 'Ready to run', value: readiness.blockers.length === 0 ? 'Yes' : 'No', tone: 'info' as const },
      ];
    default:
      return section.metrics;
  }
};

const ScheduleSettingsPage: FC<ScheduleSettingsPageProps> = ({ sectionId }) => {
  const bootstrap = getCaresShellBootstrap();
  const location = useLocation();
  const navigate = useNavigate();
  const currentSection =
    scheduleSettingsSections.find((section) => section.id === sectionId) ??
    scheduleSettingsSections[0];
  const [overview, setOverview] = useState<ScheduleSettingsOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [, setSectionPendingDrafts] = useState(0);
  const [hasUnsavedReviewChanges, setHasUnsavedReviewChanges] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadOverview = async () => {
      setLoading(true);
      setError(null);
      setOverview(null);
      setSectionPendingDrafts(0);
      setHasUnsavedReviewChanges(false);

      try {
        const response = await fetch(`/api/v1/secure/schedule-settings/overview${location.search}`, {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
        });

        if (response.status === 403) {
          throw new Error('You are not authorized to view this company and Schedule Group.');
        }
        if (!response.ok) {
          throw new Error(`Schedule Settings overview request failed (${response.status}).`);
        }

        const payload = (await response.json()) as ScheduleSettingsOverviewResponse;

        if (!cancelled) {
          setOverview(payload);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError instanceof Error ? requestError.message : 'Unable to load Schedule Settings.');
          setOverview(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadOverview();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!overview?.selected_group || currentSection.id === 'overview') return;
    const params = new URLSearchParams(location.search);
    const selectedCompany = overview.selected_company.slug;
    const selectedGroup = overview.selected_group.slug;
    if (params.get('company') === selectedCompany && params.get('group') === selectedGroup) return;
    params.set('company', selectedCompany);
    params.set('group', selectedGroup);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  }, [currentSection.id, location.pathname, location.search, navigate, overview]);

  const metrics = sectionMetrics(currentSection, overview);
  const tenantQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    if (overview?.selected_company.slug && !params.has('company')) params.set('company', overview.selected_company.slug);
    if (overview?.selected_group?.slug && !params.has('group')) params.set('group', overview.selected_group.slug);
    return params.toString();
  }, [location.search, overview]);
  const showImportWorkspace = currentSection.id === 'imports';
  const showStructureRosterWorkspace = ['locations', 'services', 'providers', 'roles-shifts'].includes(currentSection.id);
  const structureDomain = showStructureRosterWorkspace
    ? currentSection.id as 'locations' | 'services' | 'providers' | 'roles-shifts'
    : 'locations';
  const showGroupSelector = currentSection.id !== 'overview';
  const showAdvancedReadOnlyWorkspace = advancedReadOnlySectionIds.has(currentSection.id);
  const reviewKind = ({ locations: 'location', services: 'service', providers: 'provider', 'roles-shifts': 'role_shift', eligibility: 'eligibility', targets: 'target', rules: 'rule' } as const)[currentSection.id as 'locations' | 'services' | 'providers' | 'roles-shifts' | 'eligibility' | 'targets' | 'rules'];
  const requestedRuleCategory = new URLSearchParams(location.search).get('rule-category');
  const ruleCategory: RuleCategory = ruleFilters.some(filter => filter.id === requestedRuleCategory)
    ? requestedRuleCategory as RuleCategory
    : 'all';
  const editableResource = ['locations', 'services', 'providers'].includes(currentSection.id)
    ? currentSection.id as 'locations' | 'services' | 'providers'
    : null;
  const bulkWorksheetKind = bulkWorksheetKinds[currentSection.id];
  const switchScope = (key: 'company' | 'group', value: string) => {
    if (hasUnsavedReviewChanges && !window.confirm('Discard unsaved row selections or edits and switch scheduling scope?')) {
      return;
    }
    const params = new URLSearchParams(location.search);
    if (value) params.set(key, value); else params.delete(key);
    if (key === 'company') params.delete('group');
    if (key === 'group' && value && overview?.selected_company.slug) {
      void updateLastSelectedScheduleGroup(overview.selected_company.slug, value).catch(() => undefined);
    }
    setHasUnsavedReviewChanges(false);
    navigate(`${location.pathname}?${params.toString()}`);
  };

  const guardSectionNavigation = (event: MouseEvent<HTMLAnchorElement>) => {
    if (hasUnsavedReviewChanges && !window.confirm('Discard unsaved row selections or edits and leave this settings section?')) {
      event.preventDefault();
      return;
    }

    setHasUnsavedReviewChanges(false);
  };

  const switchRuleCategory = (nextCategory: RuleCategory) => {
    if (hasUnsavedReviewChanges && !window.confirm('Discard unsaved rule review changes and switch filters?')) return;
    const params = new URLSearchParams(location.search);
    if (nextCategory === 'all') params.delete('rule-category'); else params.set('rule-category', nextCategory);
    setHasUnsavedReviewChanges(false);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  };

  const departmentPortfolio = useMemo(() => {
    const groups = overview?.group_portfolio ?? [];
    const buckets = new Map<string, { id: string; name: string; groups: typeof groups }>();

    groups.forEach(group => {
      const id = group.department ? String(group.department.id) : 'unassigned';
      const bucket = buckets.get(id) ?? {
        id,
        name: group.department?.name ?? 'Unassigned Schedule Groups',
        groups: [],
      };
      bucket.groups.push(group);
      buckets.set(id, bucket);
    });

    return Array.from(buckets.values()).sort((left, right) => {
      if (left.id === 'unassigned') return 1;
      if (right.id === 'unassigned') return -1;
      return left.name.localeCompare(right.name);
    });
  }, [overview?.group_portfolio]);

  const readinessTone = (status: string) => {
    if (status === 'Active') return 'success';
    if (status === 'Needs attention') return 'danger';
    if (status === 'Draft' || status === 'Files uploaded') return 'warning';
    return 'secondary';
  };

  if (!bootstrap.authenticated) {
    return (
      <>
        <CaresPageHeader homepage="Scheduling" activepage="Scheduling" page={currentSection.navLabel} />
        <Alert variant="danger" className="custom-card">
          Sign in to CARES to access the Scheduling workspace.
          <Link to="/login" className="alert-link ms-1">
            Sign in
          </Link>
          to continue.
        </Alert>
      </>
    );
  }

  if (!bootstrap.navigation.scheduleSettings) {
    return (
      <>
        <CaresPageHeader homepage="Scheduling" activepage="Scheduling" page="Access Required" />
        <Alert variant="warning" className="custom-card">
          Schedule settings stay limited to platform admins and group-scoped scheduler operators. Your current login does not have that scheduling configuration scope.
        </Alert>
      </>
    );
  }

  return (
    <div className="schedule-settings-page">
      <CaresPageHeader homepage="Scheduling" activepage="Scheduling" page={currentSection.navLabel} />

      {showGroupSelector ? (
        <Card className="custom-card mb-3">
          <Card.Body className="p-3">
            <div className="d-flex flex-column flex-xl-row align-items-xl-center gap-3">
              <div style={{ minWidth: '18rem' }}>
                <Form.Label className="text-uppercase text-muted fs-11 fw-semibold mb-1">Schedule group</Form.Label>
                <Form.Select
                  size="sm"
                  aria-label="Select schedule group"
                  value={overview?.selected_group?.slug ?? ''}
                  disabled={!overview}
                  onChange={event => switchScope('group', event.target.value)}
                >
                  {!overview?.available_groups.length ? <option value="">No schedule groups configured</option> : null}
                  {overview?.available_groups.map(group => (
                    <option key={group.id} value={group.slug}>{displayScheduleName(group.name)}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="d-flex flex-wrap gap-2 flex-grow-1">
                {overview?.available_groups.map(group => (
                  <button
                    type="button"
                    key={group.id}
                    className={`btn btn-sm ${overview.selected_group?.id === group.id ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => switchScope('group', group.slug)}
                  >
                    {displayScheduleName(group.name)}
                  </button>
                ))}
              </div>
              <Badge bg="" className="bg-primary-transparent text-primary ms-xl-auto">
                {overview?.available_groups.length ?? 0} assigned
              </Badge>
            </div>
          </Card.Body>
        </Card>
      ) : null}

      <Row className="g-4">
        <Col xl={12}>
          {currentSection.id !== 'overview' ? <Card className="custom-card schedule-settings-page__title-card">
            <div className="schedule-settings-page__section-nav border-block-end-dashed p-3">
              <div className="schedule-settings-page__header-grid">
                <div className="schedule-settings-page__header-navigation">
                  <p className="text-uppercase text-muted fs-11 fw-semibold mb-2">Schedule Group Settings</p>
                  <Nav as="ul" variant="pills" className="mb-0 tab-style-6 gap-2 flex-wrap" aria-label="Schedule Group Settings sections">
                    {scheduleSettingsSections.filter(section => section.id !== 'overview').map(section => (
                      <Nav.Item as="li" key={section.id}>
                        <Link
                          className={`nav-link text-nowrap${currentSection.id === section.id ? ' active' : ''}`}
                          to={`${section.path}${location.search}`}
                          onClick={guardSectionNavigation}
                          aria-current={currentSection.id === section.id ? 'page' : undefined}
                        >
                          {section.navLabel}
                        </Link>
                      </Nav.Item>
                    ))}
                  </Nav>
                </div>
                <aside className="schedule-settings-page__glance" aria-label={`${currentSection.navLabel} at a glance`}>
                  <p className="text-uppercase text-muted fs-11 fw-semibold mb-2">At a glance</p>
                  <div className="schedule-settings-page__glance-metrics">
                    {metrics.slice(0, 3).map(metric => (
                      <div className="schedule-settings-page__glance-metric" key={metric.label}>
                        <span>{metric.label}</span>
                        <strong className={`text-${toneClassMap[metric.tone]}`}>{metric.value}</strong>
                      </div>
                    ))}
                  </div>
                </aside>
              </div>
            </div>
            {currentSection.id === 'rules' ? <div className="p-3">
              <p className="text-uppercase text-muted fs-11 fw-semibold mb-2">Rule filters</p>
              <div className="d-flex flex-wrap gap-2" role="tablist" aria-label="Rule categories">
                {ruleFilters.map(filter => <button
                  type="button"
                  role="tab"
                  aria-selected={ruleCategory === filter.id}
                  key={filter.id}
                  className={`btn btn-sm ${ruleCategory === filter.id ? 'btn-primary' : 'btn-light'}`}
                  onClick={() => switchRuleCategory(filter.id)}
                >{filter.label}</button>)}
              </div>
            </div> : null}
          </Card> : null}

          {overview && !overview.selected_group && overview.unassigned_onboarding.candidates > 0 && (
            <Alert variant="warning" className="py-2 px-3 mb-3">
              <strong>{overview.unassigned_onboarding.candidates} onboarding drafts are not assigned to a schedule group.</strong>{' '}
              Company Admin can assign their import packages from{' '}
              <Link to={`/admin-settings/overview?company=${encodeURIComponent(overview.selected_company.slug)}#onboarding-draft-routing`} className="alert-link">
                Company Admin → Overview
              </Link>.
            </Alert>
          )}

          {loading && (
            <Alert variant="light" className="d-flex align-items-center gap-2 mt-4 mb-0">
              <Spinner animation="border" size="sm" />
              Loading schedule settings for this tenant.
            </Alert>
          )}

          {error && (
            <Alert variant="warning" className="mt-4 mb-0">
              {error}
            </Alert>
          )}

          <div className="d-flex flex-column">
            <Row className="g-4 mt-0">
              <Col xxl={12}>
                {showStructureRosterWorkspace ? (
                  overview?.selected_group
                    ? <StructureRosterWorkspace key={`${structureDomain}:${tenantQuery}`} tenantQuery={tenantQuery} initialDomain={structureDomain} onPendingChange={setSectionPendingDrafts} onDirtyChange={setHasUnsavedReviewChanges} />
                    : <Alert variant="warning">Select a Schedule Group to review Structure &amp; Roster drafts. Tenant-wide active records remain isolated by company.</Alert>
                ) : reviewKind ? (
                  <div>
                    {editableResource ? <ResourceCrudWorkspace resource={editableResource} tenantQuery={tenantQuery} /> : null}
                    {!editableResource && currentSection.id === 'rules' ? <AdvancedReadOnlyWorkspace section={currentSection} tenantQuery={tenantQuery} overview={overview} /> : null}
                    <EvidenceReviewWorkspace kind={reviewKind} tenantQuery={tenantQuery} ruleCategory={reviewKind === 'rule' ? ruleCategory : undefined} onPendingChange={setSectionPendingDrafts} onDirtyChange={setHasUnsavedReviewChanges} />
                  </div>
                ) : showImportWorkspace ? (
                  overview?.selected_group
                    ? <div className="d-flex flex-column"><ImportWizardWorkspace tenantQuery={tenantQuery} /><ImportContractPanel tenantQuery={tenantQuery} /></div>
                    : <Alert variant="warning">Select a Schedule Group before uploading or reviewing files. Imports are never shared across Schedule Groups.</Alert>
                ) : showAdvancedReadOnlyWorkspace ? (
                  <AdvancedReadOnlyWorkspace
                    section={currentSection}
                    tenantQuery={tenantQuery}
                    overview={overview}
                  />
                ) : currentSection.id === 'overview' && overview ? (
                  <div className="d-flex flex-column gap-4">
                    {overview.group_portfolio.length === 0 ? <Alert variant="info" className="mb-0">No schedule groups are available for this company.</Alert> : departmentPortfolio.map(department => (
                      <section className="d-flex flex-column gap-3" key={department.id} aria-labelledby={`department-${department.id}`}>
                        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-2 border-bottom pb-2">
                          <div>
                            <h5 className="mb-1" id={`department-${department.id}`}>{department.name}</h5>
                            <span className="text-muted fs-12">{department.groups.length} {department.groups.length === 1 ? 'Schedule Group' : 'Schedule Groups'}</span>
                          </div>
                          <div className="d-flex flex-wrap gap-2">
                            {Array.from(new Set(department.groups.map(group => group.readiness_status))).map(status => (
                              <Badge bg={status === 'Active' ? 'success' : 'warning'} key={status}>
                                {department.groups.filter(group => group.readiness_status === status).length} {status}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <Row className="g-3 schedule-settings-page__portfolio-grid">
                          {department.groups.map(group => (
                            <Col sm={6} xl={4} xxl={3} key={group.id}>
                              <Card className="custom-card h-100 mb-0 schedule-settings-page__portfolio-card">
                                <Card.Header className="p-3">
                                  <div className="d-flex align-items-start justify-content-between gap-2">
                                    <div className="min-w-0">
                                      <div className="card-title mb-1 text-truncate" title={group.name}>{group.name}</div>
                                      <span className="text-muted fs-11">{group.department?.name ?? 'Unassigned department'}</span>
                                    </div>
                                    <Badge bg={readinessTone(group.readiness_status)}>{group.readiness_status}</Badge>
                                  </div>
                                </Card.Header>
                                <Card.Body className="p-3 d-flex flex-column gap-3">
                                  <div className="schedule-settings-page__portfolio-facts">
                                    <div><span>Published through</span><strong>{group.operations.published_through ?? 'Not published'}</strong></div>
                                    <div><span>Next draft</span><strong>{group.operations.next_draft?.status ?? 'None queued'}</strong></div>
                                    <div><span>Pending requests</span><strong>{group.operations.request_counts.pending ?? 0}</strong></div>
                                    <div><span>Chronos state</span><strong>{group.operations.chronos_run?.review_state ?? 'No run'}</strong></div>
                                  </div>
                                  <div className="d-flex gap-2 mt-auto">
                                    <Link className="btn btn-sm btn-primary flex-fill" to={`/scheduling/group-schedule?company=${encodeURIComponent(overview.selected_company.slug)}&group=${encodeURIComponent(group.slug)}`}>Schedule</Link>
                                    <Link className="btn btn-sm btn-outline-primary flex-fill" to={`/scheduling/group-tallies?company=${encodeURIComponent(overview.selected_company.slug)}&group=${encodeURIComponent(group.slug)}`}>Tallies</Link>
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </section>
                    ))}
                    {overview.group_portfolio.length > 0 ? (
                      <Card className="custom-card mb-0">
                        <Card.Header>
                          <div className="card-title mb-1">Readiness blockers</div>
                          <p className="text-muted fs-12 mb-0">
                            Missing setup requirements that must be completed before CARES can reliably generate and publish each Schedule Group.
                          </p>
                        </Card.Header>
                        <Card.Body>
                          {overview.group_portfolio.some(group => group.blockers.length > 0) ? (
                            <div className="d-flex flex-column gap-3">
                              {overview.group_portfolio.filter(group => group.blockers.length > 0).map(group => (
                                <div className="border rounded p-3" key={group.id}>
                                  <strong>{group.name}</strong>
                                  <ul className="mb-0 mt-2 ps-3">
                                    {group.blockers.map(blocker => <li key={blocker} className="mb-1 fs-12">{blocker}</li>)}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          ) : <div className="text-success">No Schedule Groups have setup blockers.</div>}
                        </Card.Body>
                      </Card>
                    ) : null}
                  </div>
                ) : (
                  <Card className="custom-card h-100">
                    <Card.Header>
                      <div className="card-title">{currentSection.workspaceTitle}</div>
                    </Card.Header>
                    <Card.Body>
                      <p className="text-muted mb-4">{currentSection.workspaceDescription}</p>
                      <div className="border rounded-3 p-3 bg-light">
                        <p className="fw-semibold mb-2">What to do now</p>
                        <ul className="mb-0 ps-3">
                          {currentSection.checklist.map((item) => (
                            <li key={item} className="mb-2 text-muted">
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {overview?.selected_group && bulkWorksheetKind ? (
                  <div className="mt-4">
                    <ImportWizardWorkspace
                      tenantQuery={tenantQuery}
                      fixedKind={bulkWorksheetKind}
                      cardTitle="Bulk Setup"
                    />
                  </div>
                ) : null}

                {currentSection.id === 'locations' ? <div className="mt-4"><LocationHelpTip /></div> : null}
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ScheduleSettingsPage;
